import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type FoodRelation = 'ANY' | 'BEFORE_30M' | 'WITH_MEAL' | 'AWAY_120M';
export type DoseStatus = 'PLANNED' | 'TAKEN' | 'MISSED' | 'SNOOZED';

export interface Preset {
  id: string;
  owner_user_id: string | null;
  name: string;
  tag: string;
  food_relation: FoodRelation;
  priority: number;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface Schedule {
  id: string;
  user_id: string;
  title: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ScheduleEntry {
  id: string;
  schedule_id: string;
  preset_id: string | null;
  group_id: string | null;
  desired_offset_min: number;
  note: string | null;
  created_at: string;
  updated_at: string;
  preset?: Preset;
  group?: PresetGroup & { presets: Preset[] };
}

export interface GapRule {
  id: string;
  schedule_id: string;
  source_tag: string | null;
  source_group_id: string | null;
  relation: string;
  target_tag: string | null;
  target_group_id: string | null;
  min_gap_min: number;
  created_at: string;
  updated_at: string;
  source_group?: PresetGroup & { presets: Preset[] };
  target_group?: PresetGroup & { presets: Preset[] };
}

export interface DayPlan {
  id: string;
  user_id: string;
  wake_time: string;
  generated_at: string;
  recompute_count: number;
  created_at: string;
}

export interface DayPlanItem {
  id: string;
  day_plan_id: string;
  schedule_entry_id: string;
  preset_snapshot: {
    name: string;
    tag: string;
    priority: number;
    food_relation: FoodRelation;
    description?: string;
  };
  desired_time: string;
  planned_time: string;
  is_next_day: boolean;
  shift_minutes: number;
  status: DoseStatus;
  actual_time: string | null;
  conflict_notes: string[];
  source_group_id: string | null;
  source_group_snapshot: {
    name: string;
    description?: string;
  } | null;
  group_instance_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PresetGroup {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface PresetGroupMember {
  id: string;
  group_id: string;
  preset_id: string;
  created_at: string;
}

export interface UserStats {
  user_id: string;
  current_streak_days: number;
  longest_streak_days: number;
  total_doses_taken: number;
  perfect_days_count: number;
  last_perfect_day: string | null;
  facts_viewed_count: number;
  created_at: string;
  updated_at: string;
}

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  sort_order: number;
  created_at: string;
}

export interface UserBadge {
  id: string;
  user_id: string;
  badge_id: string;
  earned_at: string;
  progress_data: Record<string, any>;
  badge?: BadgeDefinition;
}

export interface SupplementFact {
  id: string;
  fact_text: string;
  category: string;
  related_tags: string[];
  created_at: string;
}
